'use strict';

/**
 * Import type definitions allowing VS Code to show IntelliSense.
 * @typedef {import('./NavigationHandler').default} NavigationHandler
 * @typedef {import('./CommonMethods').default} CommonMethods
 */

import ClassLogger from 'ClassLogger';
import {
    browserSupportsWebAuthn,
    browserSupportsWebAuthnAutofill,
    // platformAuthenticatorIsAvailable,
    startRegistration,
    startAuthentication
} from '@simplewebauthn/browser';

class WebAuthn {
    getClassName () {
        return 'WebAuthn';
    }

    /**
     * @param {CommonMethods} commonMethods
     * @param {NavigationHandler} navigationHandler
     */
    constructor (commonMethods, navigationHandler) {
        const self = this;
        self.logger = ClassLogger(self, true); // set second parameter to false to disable logging
        this.navigationHandler = navigationHandler;
        this.commonMethods = commonMethods;

        this.urls = {
            registration: window.location.origin + '/webauthn/registrationoptions',
            authentication: window.location.origin + '/webauthn/authenticationoptions',
        };

        this.navigationHandler
            .on('ready', () => this.init())
            .on('render', () => this.init());
    }

    init () {
        if (document.querySelector('form[data-loginform]')) {
            this.initLogin();
            return;
        }

        if (document.querySelector('form[data-passkeyform]')) {
            this.initPasskeyCreation();
            return;
        }

        this.logger.log('Nothing to do here...');
    }

    initPasskeyCreation () {
        const createButton = document.querySelector('button[data-createpasskey]');
        if (!createButton) {
            return;
        }

        if (!browserSupportsWebAuthn()) {
            createButton.classList.add('u-hide');
            alert('Dein Browser unterstützt leider keine Passkeys.');
            return;
        }

        this.logger.log('Offering to add passkey');
        createButton.addEventListener('click', async (e) => {
            e.preventDefault();

            const form = document.querySelector('form[data-passkeyform]');
            this.handleAction(
                form,
                createButton,
                async () => {
                    const data = await this.fetchJson(this.urls.registration);
                    this.logger.log('Received passkey authentication data', data);
                    return data;
                },
                async (data) => {
                    return startRegistration(data);
                },
            );
        });
    }

    async handleAction (form, button, startHandler, finishHandler) {
        try {
            button.dataset.originalLabel = button.textContent;
            button.textContent = button.dataset.loadingText || 'Bitte warten…';
            button.classList.add('is-loading');

            const data = await startHandler();
            this.logger.log('Received start data', data);

            button.textContent = 'Warte auf Authenticator';

            // Pass the options to the authenticator and wait for a response
            const attResp = await finishHandler(data);

            const passkeyInput = this.commonMethods.markupToElement('<input type="hidden" name="passkey">');
            passkeyInput.value = JSON.stringify(attResp);

            button.textContent = button.dataset.loadingText || 'Bitte warten…';

            form.appendChild(passkeyInput);
            // form.requestSubmit(); -> does not include our 'submitVar' field (bypasses navigation handler)
            form.querySelector('button[type="submit"]').click();
        } catch (error) {
            this.logger.error('Error during passkey action', error);

            // show error in button
            button.classList.remove('u-hide', 'is-loading');
            button.textContent = 'Es trat ein Fehler auf!';
            button.classList.add('u-shake-horizontal', 'c-button--alert');
            button.addEventListener('animationend', (ev) => {
                setTimeout(() => {
                    // reset button to initial state
                    button.classList.remove('u-shake-horizontal', 'c-button--alert');
                    button.textContent = button.dataset.originalLabel;
                }, 1000);
            });
        }
    }

    async initLogin () {
        const webAuthnSupported = browserSupportsWebAuthn();

        const form = document.querySelector('form[data-loginform]');
        this.logger.log('Login form found', { form, webAuthnSupported });

        const loginButton = form.querySelector('[data-passkey-loginbutton]');
        if (!loginButton) {
            this.logger.warn('No passkey button present. Stopping webauthn init.');
            return;
        }

        // Add query param for webauthn
        const url = new URL(location.href);
        url.searchParams.set('supportsPasskeys', webAuthnSupported ? '1' : '0');
        form.action = url.toString();

        if (webAuthnSupported) {
            // show button to manually login with passkey
            if (loginButton) {
                loginButton.addEventListener('click', (event) => {
                    event.preventDefault();
                    this.login(false);
                    return false;
                });
            }

            browserSupportsWebAuthnAutofill().then((supported) => {
                this.logger.log('conditional UI support', supported);
                if (supported) {
                    // If autofill is supported, start login
                    this.login(true);
                } else {
                    loginButton.classList.remove('u-hide');
                }
            }).catch(e => {
                this.logger.error('Webauthn autofill failed', e);
                loginButton.classList.remove('u-hide');
            });
        }
    }

    async login (conditionalUI = false) {
        this.logger.log('Start passkey login', { conditionalUI });

        const form = document.querySelector('form[data-loginform]');
        const button = form.querySelector('[data-passkey-loginbutton]');

        this.handleAction(
            form,
            button,
            async () => {
                const data = await this.fetchJson(this.urls.authentication);
                this.logger.log('Received passkey authentication data', data);
                return data;
            },
            async (data) => {
                const result = await startAuthentication(data, conditionalUI);
                this.logger.log('Finished authentication with passkey', { result });
                // Remove any 'required' attributes to pass validation
                form.querySelectorAll('input[required]').forEach(input => input.removeAttribute('required'));
                return result;
            },
        );
    }

    async fetchJson (url) {
        return await (await fetch(url + '')).json();
    }
}

export default WebAuthn;
